/**
 * Merchant store create / update / retire mutations.
 *
 * Images travel as base64 strings on the create and update inputs rather than
 * through a separate upload: the backend stores the bytes in the same
 * transaction as the store, so a rejected write never leaves an orphan image.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useCallback } from 'react';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

const MERCHANT_STORE_FIELDS = graphql`
  fragment useMerchantStoreMutations_fields on MerchantStore {
    id
    oldId
    name
    websiteUrl
    lat
    lng
    primaryColor
    secondaryColor
    organizationId
    state
    logo {
      id
      url
    }
    banner {
      id
      url
    }
  }
`;

const CreateMerchantStoreMutation = graphql`
  mutation useMerchantStoreMutationsCreateMutation($input: CreateMerchantStoreInput!) {
    createMerchantStore(input: $input) {
      result {
        ...useMerchantStoreMutations_fields @relay(mask: false)
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const UpdateMerchantStoreMutation = graphql`
  mutation useMerchantStoreMutationsUpdateMutation(
    $id: ID!
    $input: UpdateMerchantStoreInput
  ) {
    updateMerchantStore(id: $id, input: $input) {
      result {
        ...useMerchantStoreMutations_fields @relay(mask: false)
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const RemoveMerchantStoreMutation = graphql`
  mutation useMerchantStoreMutationsRemoveMutation($id: ID!) {
    removeMerchantStore(id: $id) {
      result {
        id
        state
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

// Silence the unused-binding lint while keeping the fragment colocated.
void MERCHANT_STORE_FIELDS;

// ============================================================================
// Types
// ============================================================================

export interface MerchantStoreMutationError {
  code?: string | null;
  fields?: readonly string[] | null;
  message?: string | null;
  shortMessage?: string | null;
  vars?: unknown;
}

export interface MutationResult<T> {
  data: T | null;
  errors: MerchantStoreMutationError[];
  isSuccess: boolean;
}

export interface CreateMerchantStoreInput {
  name: string;
  organizationId: string;
  websiteUrl?: string | null;
  lat?: number | null;
  lng?: number | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  /** Raw base64, no `data:` prefix. Required: the column is NOT NULL. */
  logoBase64: string;
  bannerBase64: string;
}

export interface UpdateMerchantStoreInput {
  name?: string;
  websiteUrl?: string | null;
  lat?: number | null;
  lng?: number | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  /** Omit to keep the current image. */
  logoBase64?: string;
  bannerBase64?: string;
}

function unwrap(payload: any): MutationResult<any> {
  const errors = (payload?.errors ?? []) as MerchantStoreMutationError[];
  return {
    data: payload?.result ?? null,
    errors,
    isSuccess: errors.length === 0 && payload?.result != null,
  };
}

function failure(error: Error): MutationResult<any> {
  return { data: null, errors: [{ message: error.message }], isSuccess: false };
}

// ============================================================================
// Hooks
// ============================================================================

export function useCreateMerchantStoreMutation() {
  const [commit, isInFlight] = useMutation<any>(CreateMerchantStoreMutation);

  const createMerchantStore = useCallback(
    (input: CreateMerchantStoreInput) =>
      new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any) => resolve(unwrap(response?.createMerchantStore)),
          onError: (error: Error) => resolve(failure(error)),
        });
      }),
    [commit]
  );

  return { createMerchantStore, isLoading: isInFlight };
}

export function useUpdateMerchantStoreMutation() {
  const [commit, isInFlight] = useMutation<any>(UpdateMerchantStoreMutation);

  const updateMerchantStore = useCallback(
    (id: string, input: UpdateMerchantStoreInput) =>
      new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { id, input },
          onCompleted: (response: any) => resolve(unwrap(response?.updateMerchantStore)),
          onError: (error: Error) => resolve(failure(error)),
        });
      }),
    [commit]
  );

  return { updateMerchantStore, isLoading: isInFlight };
}

export function useRemoveMerchantStoreMutation() {
  const [commit, isInFlight] = useMutation<any>(RemoveMerchantStoreMutation);

  const removeMerchantStore = useCallback(
    (id: string) =>
      new Promise<MutationResult<any>>((resolve) => {
        commit({
          variables: { id },
          onCompleted: (response: any) => resolve(unwrap(response?.removeMerchantStore)),
          onError: (error: Error) => resolve(failure(error)),
        });
      }),
    [commit]
  );

  return { removeMerchantStore, isLoading: isInFlight };
}
